import { faBars, faTableCellsLarge } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "@material-ui/core"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { useCallback, useState } from "react"
import { ClientFragment } from "../../generated/graphql"
import useClientFeatureFlags from "../../hooks/useClientFeatureFlags"
import useDBTDiaryOverview from "../../hooks/useDBTDiaryOverview"
import { colors } from "../../utils/theme"
import Button from "../atoms/button/Button"
import PageControl from "../atoms/DBT/PageControl"
import Toggle from "../atoms/Toggle"
import DBTTableKey from "../molecules/DBTTableKey"
import DisabledActionTooltip from "../molecules/DisabledActionTooltip"
import GenericTable from "../molecules/GenericTable"
import CustomUrgesContent from "./CustomUrgesContent"

const SCHEDULING_DISABLED_TOOLTIP =
  "This action is disabled because the client is disconnected."

/**
 * Displays the DBT diary card overview for a client, including weekly check-ins,
 * urges and behaviors, emotions, and skills usage tables.
 *
 * @param props.client - The client whose diary data should be rendered.
 * @param props.useSplitTableLayout - When true, renders a combined
 * Urges & Emotions table with a visual split; when false, uses the original
 * two-table layout.
 */
export default function DBTDiaryCardOverview(props: {
  client: ClientFragment
  useSplitTableLayout?: boolean
  canSchedule?: boolean
}) {
  const { client, useSplitTableLayout = true, canSchedule = true } = props
  const classes = useStyles()
  const queryClient = useQueryClient()
  const [enableErrorMessage, setEnableErrorMessage] = useState<string | null>(
    null
  )
  const [useSplitLayout, setUseSplitLayout] =
    useState<boolean>(useSplitTableLayout)
  const { dbtDiary } = useClientFeatureFlags(client.id)
  const {
    resetFeedback: resetDbtDiaryFeedback,
    setEnabled: setDbtDiaryEnabled,
  } = dbtDiary

  const handleEnableFeature = useCallback(async () => {
    setEnableErrorMessage(null)
    resetDbtDiaryFeedback()
    try {
      await setDbtDiaryEnabled(true)
      await queryClient.invalidateQueries({ queryKey: ["GetClientData"] })
    } catch (error) {
      console.error("Failed to enable DBT Diary feature", error)
      const message =
        error instanceof Error
          ? error.message
          : "Unable to enable the DBT Diary feature. Please try again."
      setEnableErrorMessage(message)
    }
  }, [queryClient, resetDbtDiaryFeedback, setDbtDiaryEnabled])
  const {
    loading,
    UrgesAndBehaviorTableData,
    WeeklyCheckinTableData,
    EmotionsTableData,
    OverallSkillsTableData,
    MindfulnessTableData,
    DistressToleranceTableData,
    WalkingTheMiddlePathTableData,
    EmotionalRegulationTableData,
    InterpersonalEffectivenessTableData,
    UrgesAndEmotionsSplitTableData,
    hasDailyAssessments,
    isDailyWindowLoading,
    weekPageItems,
    selectedWeekKey,
    onWeekPageChange,
    latestWeeklyCheckinCompletedAt,
    hasWeeklyCheckin,
    hasWeeklyAssessments,
  } = useDBTDiaryOverview(client.id)

  const [showCustomUrges, setShowCustomUrges] = useState(false)
  // handle case where the client does not have the DBT diary feature enabled
  const dbtDiaryEnabled =
    client.users_user_feature_flags?.dbt_diary_enabled ?? false
  const isDisconnectedReadOnly = canSchedule === false
  const disableFeatureActions = !canSchedule || dbtDiary.isUpserting
  if (!dbtDiaryEnabled) {
    return (
      <div className={classes.warningColumn}>
        <h2>DBT Diary Feature Not Enabled</h2>
        <p>The DBT Diary feature is not enabled for this client.</p>
        {/* Enable diary card */}
        <DisabledActionTooltip
          disabled={isDisconnectedReadOnly}
          text={SCHEDULING_DISABLED_TOOLTIP}
        >
          <Button
            onClick={handleEnableFeature}
            disabled={disableFeatureActions}
            aria-busy={dbtDiary.isUpserting}
            customVariant="secondary"
            style={{ width: 250 }}
          >
            {dbtDiary.isUpserting ? "Enabling..." : "Enable DBT Diary"}
          </Button>
        </DisabledActionTooltip>
        {enableErrorMessage && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <p role="alert" style={{ color: "#B00020", marginBottom: 8 }}>
              {enableErrorMessage}
            </p>
            <DisabledActionTooltip
              disabled={isDisconnectedReadOnly}
              text={SCHEDULING_DISABLED_TOOLTIP}
            >
              <Button
                onClick={handleEnableFeature}
                disabled={disableFeatureActions}
                aria-busy={dbtDiary.isUpserting}
                customVariant="secondary"
              >
                <p style={{ marginLeft: 16, marginRight: 16 }}>
                  {dbtDiary.isUpserting ? "Retrying..." : "Retry"}
                </p>
              </Button>
            </DisabledActionTooltip>
          </div>
        )}
      </div>
    )
  }

  if (!loading && !hasDailyAssessments && !hasWeeklyAssessments) {
    return (
      <div className={classes.warningColumn}>
        <h2>No DBT Diary Entries Found</h2>
        <p style={{ textAlign: "center" }}>
          Once the client completes their daily diary card or weekly check-in,
          their data will appear here automatically. Check back after the next
          submission.
        </p>
        <CustomUrgesContent client={client} canSchedule={canSchedule} />
      </div>
    )
  }

  // Wait for the selected window to finish loading before showing its
  // period-specific empty state.
  const dailyEmptyState = !isDailyWindowLoading && !hasDailyAssessments && (
    <div className={classes.warningColumn}>
      <h2>No Daily Diary Entries Found for This Period</h2>
      <p style={{ textAlign: "center" }}>
        Once the client submits a daily diary card for this seven-day period,
        their entries will appear here. Check back after their next submission.
      </p>
    </div>
  )

  const splitLayoutContent = (
    <>
      <div className={classes.header}>
        <h1 className={classes.sectionTitle}>Urges &amp; Behavior</h1>
        <div className={classes.keyRow}>
          {hasDailyAssessments && (
            <button
              type="button"
              className={classes.customUrgesToggle}
              onClick={() => setShowCustomUrges((open) => !open)}
            >
              <span>
                {showCustomUrges ? "Hide Custom Urges" : "Show Custom Urges"}
              </span>
              <span className={classes.customUrgesIcon} aria-hidden>
                {showCustomUrges ? "×" : "+"}
              </span>
            </button>
          )}
          <DBTTableKey keyType="urges" />
          <DBTTableKey keyType="meals" />
          <DBTTableKey keyType="emotions" />
        </div>
      </div>
      {dailyEmptyState}
      {showCustomUrges && hasDailyAssessments && (
        <CustomUrgesContent client={client} canSchedule={canSchedule} />
      )}
      <GenericTable
        columns={UrgesAndEmotionsSplitTableData.columns}
        rows={UrgesAndEmotionsSplitTableData.rows}
        renderCell={UrgesAndEmotionsSplitTableData.renderCell}
        splitAfterColumnIndex={
          UrgesAndEmotionsSplitTableData.splitAfterColumnIndex
        }
        groupHeaderTitles={{ right: "Emotions" }}
      />

      <div className={classes.header}>
        <h1 className={classes.sectionTitle}>Skills Used This Week</h1>
      </div>
      <div className={classes.skillsGrid}>
        <div className={classes.skillsColumn}>
          <GenericTable
            columns={MindfulnessTableData.columns}
            rows={MindfulnessTableData.rows}
            renderCell={MindfulnessTableData.renderCell}
          />
          <GenericTable
            columns={DistressToleranceTableData.columns}
            rows={DistressToleranceTableData.rows}
            renderCell={DistressToleranceTableData.renderCell}
          />
          <GenericTable
            columns={WalkingTheMiddlePathTableData.columns}
            rows={WalkingTheMiddlePathTableData.rows}
            renderCell={WalkingTheMiddlePathTableData.renderCell}
          />
        </div>
        <div className={classes.skillsColumn}>
          <GenericTable
            columns={EmotionalRegulationTableData.columns}
            rows={EmotionalRegulationTableData.rows}
            renderCell={EmotionalRegulationTableData.renderCell}
          />
          <GenericTable
            columns={InterpersonalEffectivenessTableData.columns}
            rows={InterpersonalEffectivenessTableData.rows}
            renderCell={InterpersonalEffectivenessTableData.renderCell}
          />
        </div>
      </div>
    </>
  )

  const separateLayoutContent = (
    <>
      <div className={classes.header}>
        <h1 className={classes.sectionTitle}>Urges &amp; Behavior</h1>
        <div className={classes.keyRow}>
          {hasDailyAssessments && (
            <button
              type="button"
              className={classes.customUrgesToggle}
              onClick={() => setShowCustomUrges((open) => !open)}
            >
              <span>
                {showCustomUrges ? "Hide Custom Urges" : "Show Custom Urges"}
              </span>
              <span className={classes.customUrgesIcon} aria-hidden>
                {showCustomUrges ? "×" : "+"}
              </span>
            </button>
          )}
          <DBTTableKey keyType="urges" />
          <DBTTableKey keyType="meals" />
        </div>
      </div>
      {dailyEmptyState}
      {showCustomUrges && hasDailyAssessments && (
        <CustomUrgesContent client={client} canSchedule={canSchedule} />
      )}
      <GenericTable
        columns={UrgesAndBehaviorTableData.columns}
        rows={UrgesAndBehaviorTableData.rows}
        renderCell={UrgesAndBehaviorTableData.renderCell}
      />

      <div className={classes.header}>
        <h1 className={classes.sectionTitle}>Emotions</h1>
        <div className={classes.keyRow}>
          <DBTTableKey keyType="emotions" />
        </div>
      </div>
      <GenericTable
        columns={EmotionsTableData.columns}
        rows={EmotionsTableData.rows}
        renderCell={EmotionsTableData.renderCell}
      />

      <div className={classes.header}>
        <h1 className={classes.sectionTitle}>Used Skills (overall)</h1>
        <div className={classes.keyRow}></div>
      </div>
      <GenericTable
        columns={OverallSkillsTableData.columns}
        rows={OverallSkillsTableData.rows}
        renderCell={OverallSkillsTableData.renderCell}
      />

      <div className={classes.header}>
        <h1 className={classes.sectionTitle}>Skills Used This Week</h1>
      </div>
      <GenericTable
        columns={MindfulnessTableData.columns}
        rows={MindfulnessTableData.rows}
        renderCell={MindfulnessTableData.renderCell}
      />
      <GenericTable
        columns={DistressToleranceTableData.columns}
        rows={DistressToleranceTableData.rows}
        renderCell={DistressToleranceTableData.renderCell}
      />
      <GenericTable
        columns={WalkingTheMiddlePathTableData.columns}
        rows={WalkingTheMiddlePathTableData.rows}
        renderCell={WalkingTheMiddlePathTableData.renderCell}
      />
      <GenericTable
        columns={EmotionalRegulationTableData.columns}
        rows={EmotionalRegulationTableData.rows}
        renderCell={EmotionalRegulationTableData.renderCell}
      />
      <GenericTable
        columns={InterpersonalEffectivenessTableData.columns}
        rows={InterpersonalEffectivenessTableData.rows}
        renderCell={InterpersonalEffectivenessTableData.renderCell}
      />
    </>
  )

  return (
    <div className={classes.root}>
      <div className={classes.pageHeader}>
        {weekPageItems.length > 1 && (
          <PageControl
            items={weekPageItems}
            value={selectedWeekKey}
            onChange={onWeekPageChange}
          />
        )}
        <Toggle
          options={["separate", "split"]}
          value={useSplitLayout ? "split" : "separate"}
          onChange={(next) => setUseSplitLayout(next === "split")}
          variant="icon"
          renderLabel={(value) =>
            value === "split" ? (
              <FontAwesomeIcon icon={faTableCellsLarge} />
            ) : (
              <FontAwesomeIcon icon={faBars} />
            )
          }
        />
      </div>

      <div className={classes.header}>
        <h1 className={classes.sectionTitle}>Weekly Check-In</h1>
        <div className={classes.keyRow}>
          <DBTTableKey keyType="weekly" />
        </div>
      </div>
      {hasWeeklyCheckin ? (
        <>
          <p>
            Check-in date:{" "}
            {dayjs(latestWeeklyCheckinCompletedAt).format("MMMM D, YYYY")}
          </p>
          <GenericTable
            columns={WeeklyCheckinTableData.columns}
            rows={WeeklyCheckinTableData.rows}
            renderCell={WeeklyCheckinTableData.renderCell}
          />
        </>
      ) : (
        <p>
          {hasWeeklyAssessments
            ? "No completed weekly check-in was submitted for this seven-day period."
            : "No completed weekly check-in has been submitted yet."}
        </p>
      )}
      {useSplitLayout ? splitLayoutContent : separateLayoutContent}
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    flex: 1,
    borderRadius: 4,
    backgroundColor: "#FCFCFC",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "hidden",
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 8,
  },
  header: {
    flexDirection: "row",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    minWidth: 0,
  },
  keyRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 16,
    flexWrap: "wrap",
    minWidth: 0,
  },
  sectionTitle: {
    margin: 0,
    color: "#737373",
  },
  dateRange: {
    marginLeft: 12,
    color: "#737373",
    fontSize: 16,
    fontWeight: 400,
  },
  warningColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "100%",
    padding: theme.spacing(2),
    boxSizing: "border-box",
  },
  skillsGrid: {
    display: "flex",
    flexDirection: "column",
    columnGap: 32,
    rowGap: 16,
    alignItems: "stretch",
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      alignItems: "flex-start",
    },
  },
  skillsColumn: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
  },
  customUrgesToggle: {
    display: "inline-flex",
    gap: 4,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    color: colors.primary.shadow,
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "20px",
    cursor: "pointer",
    padding: 0,
  },
  customUrgesIcon: {
    fontSize: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    color: colors.primary.shadow,
    transform: "translateY(-1px)",
  },
}))
